import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'

const t = buildTranslate('recovery_ai.gaming_review.threshold_badge')

type BadgeStyle = {
  backgroundColor: string
  color: string
}

// Severity treatment for the count-vs-threshold pill, carried by BOTH the number
// text ("3 of 3") and the color (never color alone — the numbers convey the
// meaning): OVER the limit = red (cayenne), exactly AT the limit = amber
// (eggYolk), and neutral grey when below the limit or when the limit isn't known
// yet (the auto_resolution_threshold config is absent until the nexus companion
// ships — the badge then shows count-only). Pure + exported so the styling logic
// is unit-testable (styletron css() emits nothing under renderWithStore).
export const thresholdBadgeStyle = (
  count: number,
  threshold?: number
): BadgeStyle => {
  if (threshold !== undefined && count > threshold) {
    return { backgroundColor: palette.cayenne10, color: palette.cayenne50 }
  }
  if (threshold !== undefined && count === threshold) {
    return { backgroundColor: palette.eggYolk10, color: palette.eggYolk50 }
  }
  return { backgroundColor: palette.grey20, color: palette.grey70 }
}

type Props = {
  count: number
  // The merchant's configured auto-resolution limit. Absent until the nexus
  // companion ships the config column — the badge then shows the count only.
  threshold?: number
}

// An at-a-glance pill showing how a flagged customer's auto-resolution count
// compares to the merchant's configured limit ("3 of 3"), so the operator sees
// WHY the customer surfaced. Presentational; the pane supplies the threshold
// from getRecoveryConfig.
const ThresholdBadge = ({ count, threshold }: Props) => {
  const [css] = useStyletron()
  const { backgroundColor, color } = thresholdBadgeStyle(count, threshold)
  const hasThreshold = threshold !== undefined

  return (
    <span
      className={css({
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 600,
        backgroundColor,
        color,
      })}
    >
      {hasThreshold ? t('of', { count, threshold }) : String(count)}
    </span>
  )
}

export default ThresholdBadge
